export type StatusTone = "neutral" | "info" | "success" | "warning" | "danger" | "accent";

type StatusToneClassVariant = "subtle" | "bordered";

/**
 * Every tone states its dark background explicitly, and that is not redundant.
 *
 * These classes are almost always handed to `<Badge variant="secondary">`, whose own classes include
 * `dark:bg-zinc-400/20`. tailwind-merge resolves conflicts per modifier group, so an unprefixed `bg-*` here
 * cannot displace a `dark:bg-*` there — omit the dark background and every badge in the app renders on the
 * same zinc pill in dark mode, with only the text colour surviving to tell the statuses apart.
 */
const STATUS_TONE_CLASSNAME_BY_VARIANT: Record<StatusToneClassVariant, Record<StatusTone, string>> = {
  subtle: {
    neutral: "bg-zinc-500/15 text-zinc-700 dark:bg-zinc-400/20 dark:text-zinc-300",
    info: "bg-sky-500/15 text-sky-700 dark:bg-sky-400/20 dark:text-sky-300",
    success: "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300",
    warning: "bg-amber-500/15 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300",
    danger: "bg-rose-500/15 text-rose-700 dark:bg-rose-400/20 dark:text-rose-300",
    accent: "bg-violet-500/15 text-violet-700 dark:bg-violet-400/20 dark:text-violet-300",
  },
  bordered: {
    neutral: "border-zinc-500/40 bg-zinc-500/10 text-zinc-700 dark:border-zinc-500/40 dark:bg-zinc-400/15 dark:text-zinc-300",
    info: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:border-sky-500/40 dark:bg-sky-400/15 dark:text-sky-300",
    success:
      "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-400/15 dark:text-emerald-300",
    warning:
      "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:border-amber-500/40 dark:bg-amber-400/15 dark:text-amber-300",
    danger: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:border-rose-500/40 dark:bg-rose-400/15 dark:text-rose-300",
    accent:
      "border-violet-500/40 bg-violet-500/10 text-violet-700 dark:border-violet-500/40 dark:bg-violet-400/15 dark:text-violet-300",
  },
};

const STATUS_TONE_BY_KEY: Record<string, StatusTone> = {
  active: "success",
  approved: "success",
  available: "success",
  completed: "success",
  fulfilled: "success",
  healthy: "success",
  matched: "success",
  paid: "success",
  posted: "success",
  qualified: "success",
  resolved: "success",

  acknowledged: "accent",
  contacted: "accent",

  borrowed: "info",
  in_use: "info",
  invited: "info",
  new: "info",
  planned: "info",
  public: "info",
  scheduled: "info",
  submitted: "info",
  trial: "info",
  triaged: "info",
  visitor: "info",

  cancel_scheduled: "warning",
  high: "warning",
  in_maintenance: "warning",
  in_progress: "warning",
  medium: "warning",
  not_remitted: "warning",
  on_hold: "warning",
  partially_fulfilled: "warning",
  past_due: "warning",
  pending: "warning",
  pending_confirmation: "warning",
  upcoming: "warning",

  canceled: "danger",
  cancelled: "danger",
  critical: "danger",
  damaged: "danger",
  disabled: "danger",
  failed: "danger",
  lost: "danger",
  open: "danger",
  overdue: "danger",
  rejected: "danger",
  spam: "danger",
  suspended: "danger",
  unmatched: "danger",
  voided: "danger",

  archived: "neutral",
  closed: "neutral",
  deactivated: "neutral",
  deceased: "neutral",
  draft: "neutral",
  inactive: "neutral",
  internal: "neutral",
  low: "neutral",
  refunded: "neutral",
  retired: "neutral",
  unknown: "neutral",
};

function normalizeStatusToneKey(value: string): string {
  return value.trim().toLowerCase().replaceAll("-", "_").replaceAll(" ", "_");
}

export function resolveStatusTone(status: string): StatusTone {
  const normalizedStatus = normalizeStatusToneKey(status);

  if (normalizedStatus in STATUS_TONE_CLASSNAME_BY_VARIANT.subtle) {
    return normalizedStatus as StatusTone;
  }

  return STATUS_TONE_BY_KEY[normalizedStatus] ?? "neutral";
}

export function getStatusToneClassName(status: string, variant: StatusToneClassVariant = "subtle"): string {
  return STATUS_TONE_CLASSNAME_BY_VARIANT[variant][resolveStatusTone(status)];
}

